/**
 * 全局 WuKong IM 状态管理
 *
 * @description 管理 WuKong IM 连接状态、inbox 未读消息数，并持久化到 localStorage
 */

import { defineStore } from 'pinia'
import type { InboxUnreadCount } from '~/types/personalChat'

const STORAGE_KEY_INBOX_UNREAD = 'wukong_im_inbox_unread'

/** inbox 未读分类，与悟空 IM channelType 对应：service=2 personal=1 */
export type InboxUnreadType = 'service' | 'personal'

/** 规范化接口/本地缓存的未读结构，保证三项均为非负数字 */
const normalizeInboxUnread = (data?: InboxUnreadCount | null): Required<InboxUnreadCount> => {
  const serviceUnread = Number(data?.serviceUnread ?? 0)
  const personalUnread = Number(data?.personalUnread ?? 0)
  const totalUnread = Number(data?.totalUnread ?? serviceUnread + personalUnread)
  return { serviceUnread, personalUnread, totalUnread }
}

export interface WuKongIMState {
  isConnected: boolean
  isConnecting: boolean
  inboxUnread: Required<InboxUnreadCount>
  lastMessageObj: Record<string, unknown> | null
  sdk: any
}

export const useWuKongIMStore = defineStore('wukongIM', {
  state: (): WuKongIMState => ({
    isConnected: false,
    isConnecting: false,
    inboxUnread: normalizeInboxUnread(),
    lastMessageObj: null,
    sdk: null,
  }),

  getters: {
    /** @deprecated 兼容旧代码，等同于 totalUnread */
    unreadCount: (state): number => state.inboxUnread.totalUnread,
    serviceUnread: (state): number => state.inboxUnread.serviceUnread,
    personalUnread: (state): number => state.inboxUnread.personalUnread,
    totalUnread: (state): number => state.inboxUnread.totalUnread,
  },

  actions: {
    initFromStorage() {
      if (!import.meta.client) return
      try {
        const stored = localStorage.getItem(STORAGE_KEY_INBOX_UNREAD)
        if (stored) {
          this.inboxUnread = normalizeInboxUnread(JSON.parse(stored))
        }
      } catch (error) {
        console.error('读取 localStorage 失败:', error)
      }
    },

    persistInboxUnread() {
      if (!import.meta.client) return
      try {
        localStorage.setItem(STORAGE_KEY_INBOX_UNREAD, JSON.stringify(this.inboxUnread))
      } catch (error) {
        console.error('保存未读数到 localStorage 失败:', error)
      }
    },

    /** 接口刷新或插件初始化时写入完整 inbox 未读 */
    setInboxUnread(data: InboxUnreadCount | Record<string, unknown>) {
      this.inboxUnread = normalizeInboxUnread(data as InboxUnreadCount)
      this.lastMessageObj = { ...(data as Record<string, unknown>) }
      this.persistInboxUnread()
    },

    /** 收到 IM 推送时递增；MessageChatPanel 已读后会 decrementInboxUnread */
    incrementInboxUnread(type: InboxUnreadType, delta: number = 1) {
      const key = type === 'service' ? 'serviceUnread' : 'personalUnread'
      const next = { ...this.inboxUnread }
      next[key] = Math.max(0, next[key] + delta)
      next.totalUnread = next.serviceUnread + next.personalUnread
      this.inboxUnread = next
      this.persistInboxUnread()
    },

    decrementInboxUnread(type: InboxUnreadType, delta: number = 1) {
      this.incrementInboxUnread(type, -delta)
    },

    clearServiceUnread() {
      const next = { ...this.inboxUnread, serviceUnread: 0 }
      next.totalUnread = next.serviceUnread + next.personalUnread
      this.inboxUnread = next
      this.persistInboxUnread()
    },

    clearPersonalUnread() {
      const next = { ...this.inboxUnread, personalUnread: 0 }
      next.totalUnread = next.serviceUnread + next.personalUnread
      this.inboxUnread = next
      this.persistInboxUnread()
    },

    clearInboxUnread() {
      this.inboxUnread = normalizeInboxUnread()
      this.persistInboxUnread()
    },

    /** @deprecated 请使用 clearServiceUnread */
    clearUnreadCount() {
      this.clearServiceUnread()
    },

    /** @deprecated 请使用 setInboxUnread */
    setUnreadCount(count: number) {
      this.setInboxUnread({ serviceUnread: count, personalUnread: 0 })
    },

    /** @deprecated 请使用 incrementInboxUnread */
    incrementUnreadCount(delta: number = 1) {
      this.incrementInboxUnread('service', delta)
    },

    setConnected(connected: boolean) {
      this.isConnected = connected
    },

    setConnecting(connecting: boolean) {
      this.isConnecting = connecting
    },

    setSdk(sdk: any) {
      this.sdk = sdk
    },

    updateLastMessage(obj: Record<string, unknown>) {
      this.lastMessageObj = obj
    },
  },
})

// 消息回调管理器（全局单例）
class MessageCallbackManager {
  private static instance: MessageCallbackManager
  private callbacks: Array<(message: any) => void> = []
  private connectCallbacks: Array<() => void> = []
  private disconnectCallbacks: Array<() => void> = []

  static getInstance(): MessageCallbackManager {
    if (!MessageCallbackManager.instance) {
      MessageCallbackManager.instance = new MessageCallbackManager()
    }
    return MessageCallbackManager.instance
  }

  addMessageCallback(callback: (message: any) => void) {
    this.callbacks.push(callback)
    return () => {
      const index = this.callbacks.indexOf(callback)
      if (index > -1) {
        this.callbacks.splice(index, 1)
      }
    }
  }

  addConnectCallback(callback: () => void) {
    this.connectCallbacks.push(callback)
    return () => {
      const index = this.connectCallbacks.indexOf(callback)
      if (index > -1) {
        this.connectCallbacks.splice(index, 1)
      }
    }
  }

  addDisconnectCallback(callback: () => void) {
    this.disconnectCallbacks.push(callback)
    return () => {
      const index = this.disconnectCallbacks.indexOf(callback)
      if (index > -1) {
        this.disconnectCallbacks.splice(index, 1)
      }
    }
  }

  notifyMessage(message: any) {
    this.callbacks.forEach(cb => cb(message))
  }

  notifyConnect() {
    this.connectCallbacks.forEach(cb => cb())
  }

  notifyDisconnect() {
    this.disconnectCallbacks.forEach(cb => cb())
  }

  clearAllCallbacks() {
    this.callbacks = []
    this.connectCallbacks = []
    this.disconnectCallbacks = []
  }
}

export const messageCallbackManager = MessageCallbackManager.getInstance()
